import { useEffect, useState } from 'react';
import { useToastStore, TOAST_MS, type ToastItem } from '@/shared/store/toastStore';
import { IconCheck, IconAlertCircle, IconX } from './Icon';

function ToastRow({ t }: { t: ToastItem }) {
  const dismiss = useToastStore((s) => s.dismiss);
  // Hover or keyboard focus holds the toast; leaving restarts the 4 seconds.
  const [held, setHeld] = useState(false);
  useEffect(() => {
    if (held) return;
    const timer = setTimeout(() => dismiss(t.id), TOAST_MS);
    return () => clearTimeout(timer);
  }, [t.id, dismiss, held]);
  return (
    <div data-toast-row="" onMouseEnter={() => setHeld(true)} onMouseLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)} onBlur={() => setHeld(false)}
      className="pointer-events-auto flex items-center gap-2 rounded-md border border-info-line bg-surface px-3 py-1.5 text-body text-primary-text shadow-md animate-dialog">
      <span aria-hidden="true" className={`shrink-0 ${t.tone === 'success' ? 'text-success' : 'text-info'}`}>
        {t.tone === 'success' ? <IconCheck size={13} /> : <IconAlertCircle size={13} />}
      </span>
      <span>{t.message}</span>
      {t.action && (
        <button type="button" onClick={() => { t.action?.onClick(); dismiss(t.id); }}
          className="shrink-0 font-medium text-primary underline hover:no-underline focus-ring rounded">{t.action.label}</button>
      )}
      <button type="button" onClick={() => dismiss(t.id)} title="Dismiss" aria-label="Dismiss"
        className="shrink-0 inline-flex min-h-[24px] min-w-[24px] items-center justify-center rounded text-muted hover:text-primary-text focus-ring"><IconX size={12} /></button>
    </div>
  );
}

// Bottom-right, above the bottom band (48px) and the phone AI Companion
// button (D6; 44px in rem, so 64px clears it at Appearance XL), left of the docked AI Companion panel (--copilot-dock). While the floating panel is open, index.css moves it clear
// of the panel (html[data-copilot-float]).
// The live region is always in the page, so a toast that appears is announced.
export function ToastHost() {
  const toasts = useToastStore((s) => s.toasts);
  return (
    <div role="status" aria-live="polite" data-toast-region=""
      className="pointer-events-none fixed bottom-[64px] right-[calc(var(--copilot-dock,0px)+12px)] z-toast flex flex-col items-end gap-1.5">
      {toasts.map((t) => <ToastRow key={t.id} t={t} />)}
    </div>
  );
}
